import { PortalConfigurationType, UserProfile } from '@trnsact/trnsact-shared-types';
import { PrescreenTypes } from 'global';
import { PortalConfiguration as PortalConfigurationApiType } from '@trnsact/trnsact-shared-types';
import { tiersDefaultConfig } from 'modules/portalConfigurations/internalCreditTiers/constants';
import { recertificationDefaultConfig } from 'modules/portalConfigurations/recertification/constants';
import { adminRoles, portalConfigurationTypes } from '../pages/Prequal/constants';
import { getDefaultPortalConfigurationByType } from '../services/pcwService';
import { PortalConfiguration } from 'modules/portalConfigurations/types';

const SET_CONFIG_DATA = 'SET_CONFIG_DATA';

type State = Partial<Record<string, PortalConfigurationApiType>>;

const initialState: State = {};

const portalConfigurationReducer = (state = initialState, { type, payload }: any) => {
  switch (type) {
    case SET_CONFIG_DATA:
      return payload.reduce(
        (acc: State, portalItemConfig: PortalConfigurationApiType) => {
          acc[portalItemConfig.type!] = portalItemConfig;
          delete (acc[portalItemConfig.type!] as any)?.__typename;

          return acc;
        },
        { ...state }
      );
    default:
      return state;
  }
};

// Actions

const setConfigData = (payload: PortalConfigurationApiType[] = []) => ({
  type: SET_CONFIG_DATA,
  payload,
});

// Selectors

const configData = (state: any) => state.portalConfiguration;
const configDataByType = (state: any, type: PortalConfigurationType) => state.portalConfiguration?.[type];

const recertificationJSONConfig = (state: any) =>
  state.portalConfiguration?.[PortalConfigurationType.Recertification]?.jsonDefinition?.config ??
  recertificationDefaultConfig;

const internalCreditTiersJSONConfig = (state: any) =>
  state.portalConfiguration?.[PortalConfigurationType.InternalCreditTiers]?.jsonDefinition?.config ??
  tiersDefaultConfig;

const isTiersBuilderHidden = (state: any) => {
  const config = internalCreditTiersJSONConfig(state);
  return config?.configurationTiersHiddenFor?.includes(state.userProfile?.vendorContactRole);
};

const isTiersResultsHidden = (state: any) => {
  const config = internalCreditTiersJSONConfig(state);
  return config?.tiersResultsHiddenFor?.includes(state.userProfile?.vendorContactRole);
};
// PrescreenModule

const prescreenModuleConfig = (state: any) =>
  configDataByType(state, PortalConfigurationType.PrescreenManagement)?.jsonDefinition?.config;

const prescreenModuleType = (state: any) => prescreenModuleConfig(state)?.prescreenType;

const checkIfPrescreenHiddenConfigSetForUser = (state: any, prop: string, userProfile: UserProfile) => {
  // Check for userProfile?.vendorContactRole is a hotfix since we want to show it by default for HYUNDAI users ONLY.
  // HYUNDAI users have vendorContactRole = credit_mgr, when rest Lenders have null
  return (
    !userProfile?.vendorContactRole || prescreenModuleConfig(state)?.[prop]?.includes(userProfile?.vendorContactRole)
  );
};

const checkIfUserHaveAccessToPrescreenPage = (state: any, userProfile: UserProfile) => {
  const isHiddenByPCW = checkIfPrescreenHiddenConfigSetForUser(state, 'hiddenPrescreenPage', userProfile);
  return !isHiddenByPCW || userProfile.adminRole === adminRoles.super;
};

const checkIfUserHaveAccessToPrescreenResults = (state: any, userProfile: UserProfile) => {
  const isHiddenByPCW = checkIfPrescreenHiddenConfigSetForUser(state, 'hiddenPrescreenResults', userProfile);
  return !isHiddenByPCW || userProfile.adminRole === adminRoles.super;
};

const isPrescreenScorecardBuilderHidden = (state: any) =>
  checkIfPrescreenHiddenConfigSetForUser(state, 'hiddenPrescreenScorecardBuilder', state.userProfile);

const checkIfUserHaveAccessToSubmit = (state: any, userProfile: UserProfile) => {
  const isHiddenByPCW = checkIfPrescreenHiddenConfigSetForUser(state, 'hiddenPrescreenSubmissionAccess', userProfile);
  return !isHiddenByPCW || userProfile.adminRole === adminRoles.super;
};

const isPrescreenModuleActive = (state: any) => prescreenModuleType(state) !== PrescreenTypes.Off;

const prescreenModuleName = (state: any) =>
  prescreenModuleType(state) === PrescreenTypes.Prescreen
    ? { full: 'Prescreen', short: 'Prescreen' }
    : { full: 'Lender Waterfall', short: 'Waterfall' };

const checkIfUserHaveAccessToInvoiceAndEquipmentV2 = (state: any) =>
  state.portalConfiguration?.[PortalConfigurationType.InvoiceAndEquipment]?.jsonDefinition?.config
    ?.allowInvoiceAndEquipmentV2;

const checkAccessInventorySearch = (state: any) => {
  if (state.userProfile.adminRole === adminRoles.super) return true;

  const apiConfigData = configDataByType(state, PortalConfigurationType.Opportunities);
  const defaultConfigData = getDefaultPortalConfigurationByType(PortalConfigurationType.Opportunities);

  const mergeConfigData = { ...apiConfigData?.jsonDefinition?.config, ...defaultConfigData?.jsonDefinition?.config };
  const arrayOfVendorContactRole = mergeConfigData.hiddenInventorySearch;

  return !arrayOfVendorContactRole.includes(state.userProfile?.vendorContactRole);
};

const portalConfigurationsActions = {
  setConfigData,
};

const voManagementPortalConfigurations = (state: any) => {
  const config = state.portalConfiguration?.[portalConfigurationTypes.voManagement] ?? {};
  const apiConfigValues = config?.jsonDefinition?.config ?? {};

  return apiConfigValues;
};

const getAftermarket = (state: any) => {
  const config = state.portalConfiguration?.[portalConfigurationTypes.aftermarket] ?? {};
  return config?.jsonDefinition?.config ?? {};
};

const allowManualVoCreation = (state: any) => {
  const opportunitiesPortalConfigurations = configDataByType(state, PortalConfigurationType.Opportunities);
  return opportunitiesPortalConfigurations?.jsonDefinition.config.allowManualVoCreation;
};

const allowDealPipeline = (state: any) => {
  const opportunitiesPortalConfigurations = configDataByType(state, PortalConfigurationType.Opportunities);
  return opportunitiesPortalConfigurations?.jsonDefinition.config.allowDealPipeline ?? false;
};

const portalConfigurationsSelectors = {
  internalCreditTiersJSONConfig,
  isTiersBuilderHidden,
  isTiersResultsHidden,
  recertificationJSONConfig,
  configData,
  configDataByType,
  prescreenModuleConfig,
  prescreenModuleType,
  checkIfUserHaveAccessToPrescreenPage,
  checkIfUserHaveAccessToPrescreenResults,
  checkIfUserHaveAccessToSubmit,
  isPrescreenModuleActive,
  isPrescreenScorecardBuilderHidden,
  prescreenModuleName,
  checkIfUserHaveAccessToInvoiceAndEquipmentV2,
  checkAccessInventorySearch,
  voManagementPortalConfigurations,
  getAftermarket,
  allowManualVoCreation,
  allowDealPipeline,
};

export { portalConfigurationReducer, portalConfigurationsSelectors, portalConfigurationsActions };
